import { useEffect, useState } from "react";
import { onSnapshot, or, orderBy, query, where } from "firebase/firestore";

import { useAuth } from "../context/AuthContext";
import { bookmarksCollection, foldersCollection } from "../database";
import { Header, Item, ItemForm, Loader, Tabs } from "../components";
import ItemFolder from "../components/ItemFolder";
import About from "./About";
import EmptyCheck from "../components/EmptyCheck";

const Home = () => {
  const { authUser } = useAuth();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [showItemForm, setShowItemForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState("Fetching...");

  useEffect(() => {
    if (!authUser) return;

    // setLoading("Fetching...");
    const foldersUnsubscribe = onSnapshot(
      query(
        foldersCollection,
        or(
          where("createdBy", "==", authUser.email),
          where("accessTo", "array-contains", authUser.email)
        ),
        orderBy("name", "asc")
      ),
      (snapshot: any) => {
        setFolders(
          snapshot.docs.map((doc: any) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
        // setLoading("");
      }
    );

    return () => {
      foldersUnsubscribe();
    };
  }, [authUser]);

  useEffect(() => {
    // setLoading("Fetching bookmarks...");
    const folderIds = folders?.map((folder: any) => folder.id);
    let bookmarksQuery: any;

    // FIRESTORE "IN" QUERY DOES NOT SUPPORT EMPTY ARRAYS
    if (folderIds.length === 0) {
      bookmarksQuery = query(
        bookmarksCollection,
        where("createdBy", "==", authUser.email),
        orderBy("title", "asc")
      );
    } else {
      bookmarksQuery = query(
        bookmarksCollection,
        or(
          where("createdBy", "==", authUser.email),
          where("folderId", "in", folderIds)
        ),
        orderBy("title", "asc")
      );
    }

    const bookmarksUnsubscribe = onSnapshot(bookmarksQuery, (snapshot: any) => {
      setItems(
        snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
      setLoading("");
    });

    return () => {
      bookmarksUnsubscribe();
    };
  }, [authUser, folders]);

  const handleAddButton = () => {
    setSelectedItem(null);
    setShowItemForm(!showItemForm);
  };

  const handleSelectedItem = (data: any) => {
    setSelectedItem(data);
  };

  const clearSelectedItem = () => {
    setSelectedItem(null);
  };

  return loading ? (
    <Loader title={loading} />
  ) : (
    <>
      <Header handleAddButton={handleAddButton} isFormOpen={showItemForm} />
      {!showItemForm && (
        <Tabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      )}
      <section className='container-body'>
        {showItemForm ? (
          <ItemForm
            data={selectedItem}
            folders={folders}
            closeShowItemForm={() => setShowItemForm(false)}
            clearSelectedItem={clearSelectedItem}
          />
        ) : (
          <>
            {selectedTab === 0 && (
              <EmptyCheck
                items={[
                  ...folders.filter(
                    (folder: any) => folder.createdBy === authUser.email
                  ),
                  ...items.filter((item: any) => !item.folderId),
                ]}
                emptyState={
                  <>
                    <button
                      style={{ fontSize: "1rem", fontWeight: 600 }}
                      onClick={() => setShowItemForm(true)}
                    >
                      Add first cloudmark
                    </button>
                  </>
                }
              >
                {folders
                  ?.filter((folder: any) => folder.createdBy === authUser.email)
                  ?.map((folder: any) => (
                    <ItemFolder
                      key={folder.id}
                      id={folder.id}
                      folder={folder}
                      items={items.filter(
                        (item: any) => item.folderId === folder.id
                      )}
                      openItemForm={() => setShowItemForm(true)}
                      handleSelectedItem={handleSelectedItem}
                    />
                  ))}
                {items
                  ?.filter((item: any) => !item.folderId)
                  .map((item: any) => (
                    <Item
                      key={item.id}
                      item={item}
                      handleSelectedItem={handleSelectedItem}
                      openItemForm={() => setShowItemForm(true)}
                    />
                  ))}
              </EmptyCheck>
            )}

            {selectedTab === 1 && (
              <EmptyCheck
                items={folders.filter(
                  (folder: any) => folder.createdBy !== authUser.email
                )}
                emptyState='No shared folders'
              >
                {folders
                  ?.filter((folder: any) => folder.createdBy !== authUser.email)
                  .map((folder: any) => (
                    <ItemFolder
                      key={folder.id}
                      id={folder.id}
                      folder={folder}
                      items={items.filter(
                        (item: any) => item.folderId === folder.id
                      )}
                      openItemForm={() => setShowItemForm(true)}
                      handleSelectedItem={handleSelectedItem}
                      shared
                    />
                  ))}
              </EmptyCheck>
            )}

            {selectedTab === 2 && <About />}
          </>
        )}
      </section>
    </>
  );
};

export default Home;
